import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Common } from "../../../../common/src";
import {
  EDisplayType,
  DETAIL_TAB,
  EViewerMode,
  ProjectDetailContext,
} from "../../../pages/ProjectDetail";
import ModeToggleButton from "./Parts/ModeToggleButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { useTranslation } from "react-i18next";
import { Q3DSelect, TQ3DSelectItem } from "../../Parts/Q3DSelect";
import {
  PROJECT_STATUS_LIST,
  userSetting,
} from "../../../../common/src/modules";
import { useContext, useEffect, useState } from "react";
import { TProjectionMode } from "../../../viewer/utils/camera-utils";
import Q3DInputNumber from "../../Parts/Q3DInputNumber";
import { Viewer } from "../../../viewer/viewer";
import OverflowMenu from "../OverflowMenu";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "../../../config/config";
import { debounce } from "../../../viewer/utils/utils";
import { updateProjectionModeUserSetting } from "../../../api/user";

const PHOTOS: string = "PHOTOS";

const BackToHome = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const homeToReturn = () => {
    const search = sessionStorage.getItem("search");

    if (search) {
      const params = new URLSearchParams(search);
      navigate(`/?${params.toString()}`);
    } else {
      navigate("/");
    }
  };
  return (
    <IconButton
      onClick={() => {
        homeToReturn();
      }}
    >
      <ChevronLeftIcon style={{ color: theme.palette.base.contrast }} />
    </IconButton>
  );
};

type Props = {};

export const MenuBar = (props: Props) => {
  const [pointSize, setPointSize] = useState<number>(null);
  const {
    projectDetail,
    displayType,
    detailTab,
    setDetailTab,
    currentViewerMode,
    updateCurrentViewerMode,
    userSettings,
  } = useContext(ProjectDetailContext);
  const viewer = (window as any)["viewer"] as Viewer;

  const [projectionMode, setProjectionMode] = useState<TProjectionMode>(
    userSettings?.projectionDefault ? "perspective" : "orthographic"
  );

  const updateProjectionMode = debounce(async (newMode: TProjectionMode) => {
    if (!projectDetail?.id) return;

    const newProjectionMethod = newMode[0] === "perspective";
    const payload = { ...userSettings, projectionDefault: newProjectionMethod };

    try {
      const response = await updateProjectionModeUserSetting(payload);

      console.log(" Success update:", response);

      console.log("Success update:", newMode);
    } catch (error: any) {
      console.error("Update projection mode error:", error?.message || error);
    }
  }, 500);

  useEffect(() => {
    setProjectionMode(
      userSettings?.projectionDefault ? "perspective" : "orthographic"
    );
  }, [userSettings]);

  useEffect(() => {
    if (!viewer) return;

    if (viewer.isCameraInit()) viewer.setProjectionMode(projectionMode);
  }, [projectionMode, viewer.isCameraInit()]);

  useEffect(() => {
    setPointSize(viewer.getPointSize());
  }, []);

  useEffect(() => {
    if (
      detailTab === DETAIL_TAB.SIMPLE &&
      currentViewerMode !== EViewerMode.CALCULATION
    ) {
      updateCurrentViewerMode(EViewerMode.DEFAULT);
    }
  }, [detailTab]);

  const theme = useTheme();
  const { t, i18n } = useTranslation();

  // tab tooltip message
  const editMessage = (
    status: Common.PROJECT_STATUS_LIST,
    pointType: DETAIL_TAB
  ) => {
    if (!status) {
      return undefined;
    }

    const PROCESSING = Common.PROJECT_STATUS_ENUM.PROCESSING;
    switch (pointType) {
      case DETAIL_TAB.PHOTO:
        break;
      case DETAIL_TAB.SIMPLE:
        if (status.remove_lidar === PROCESSING)
          return t("REMOVING_POINT_CLOUD");
        if (status.point_txt_lidar === PROCESSING)
          return t("CONVERTING_TO_TEXT");
        if (status.point_splitting_lidar === PROCESSING)
          return t("POINT_CLOUD_DIVISION");
        break;
      case DETAIL_TAB.SFM:
        if (status.helmert_transformation === PROCESSING)
          return t("HELMERT_CONVERSION");
        if (status.coordinate_transformation === PROCESSING)
          return t("CONVERT_COORD_SYSTEM");
        if (status.remove_sfm === PROCESSING) return t("REMOVING_POINT_CLOUD");
        if (status.point_txt_sfm === PROCESSING) return t("CONVERTING_TO_TEXT");
        if (status.point_splitting_sfm === PROCESSING)
          return t("POINT_CLOUD_DIVISION");
        break;
      case DETAIL_TAB.HELMERT:
        if (status.helmert_transformation === PROCESSING)
          return t("HELMERT_CONVERSION");
        if (status.remove_helmert === PROCESSING)
          return t("REMOVING_POINT_CLOUD");
        if (status.point_txt_helmert === PROCESSING)
          return t("CONVERTING_TO_TEXT");
        if (status.point_splitting_helmert === PROCESSING)
          return t("POINT_CLOUD_DIVISION");
        break;
      default:
        return undefined;
    }
  };

  // tab controll
  const isShowSfmTab = (status: PROJECT_STATUS_LIST) => {
    return (
      status.sfm_processing === Common.PROJECT_STATUS_ENUM.COMPLETED ||
      status.helmert_transformation === Common.PROJECT_STATUS_ENUM.COMPLETED ||
      status.verification_helmert === Common.PROJECT_STATUS_ENUM.COMPLETED ||
      status.verification_sfm === Common.PROJECT_STATUS_ENUM.COMPLETED
    );
  };

  const isShowHelmTab = (status: PROJECT_STATUS_LIST) => {
    return (
      status.helmert_transformation === Common.PROJECT_STATUS_ENUM.COMPLETED ||
      status.verification_helmert === Common.PROJECT_STATUS_ENUM.COMPLETED
    );
  };

  const detailTabItems = () => {
    const items: TQ3DSelectItem[] = [];
    items.push({
      value: DETAIL_TAB.SIMPLE,
      label: t("LiDAR_POINT_CLOUD"),
    });
    isShowSfmTab(projectDetail?.status) &&
      items.push({
        value: DETAIL_TAB.SFM,
        label: t("ORIGINAL_SFM_POINT"),
      });
    isShowHelmTab(projectDetail?.status) &&
      items.push({
        value: DETAIL_TAB.HELMERT,
        label: t("TRANSFOR_SFM_POINT"),
      });
    items.push({ value: DETAIL_TAB.PHOTO, label: t("PHOTOS") });
    return items;
  };

  const projectionItems = [
    {
      value: "perspective",
      label: t("PERSPECTIVE_PROJECTION"),
    },
    {
      value: "orthographic",
      label: t("ORTHOGRAPHIC_PROJECTION"),
    },
  ] as TQ3DSelectItem[];

  const barHeight = "40px";

  return (
    <Box
      sx={{
        background: theme.palette.base.main,
        padding: "4px 16px",
        borderBottom: "solid 1px",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: barHeight,
          alignItems: "center",
          wordWrap: "break-word",
          minWidth: "0",
        }}
      >
        {displayType === EDisplayType.Web && <BackToHome />}
        <Typography
          variant="h6"
          component="div"
          sx={{
            color: theme.palette.base.contrast,
            overflow: "hidden",
            marginLeft: displayType === EDisplayType.Web ? "0px" : "40px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {projectDetail?.name}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          gap: "var(--space-space--4, 4px)",
          marginRight:
            projectDetail?.status.ply_upload !==
            Common.PROJECT_STATUS_ENUM.COMPLETED
              ? "0px"
              : "48px",
        }}
      >
        {editMessage(projectDetail?.status, detailTab) && (
          <Tooltip
            title={
              <span className="tool-tip-cal">
                {editMessage(projectDetail?.status, detailTab)}
              </span>
            }
            arrow
            placement="top"
            disableInteractive
          >
            <DownloadingIcon
              sx={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                margin: "4px",
                transform: "rotate(180deg)",
                color: theme.palette.primary.main,
              }}
            />
          </Tooltip>
        )}
        <Q3DSelect
          label={t("SHOW")}
          items={detailTabItems()}
          value={detailTab}
          setValue={(v) => {
            setDetailTab(v as DETAIL_TAB);
          }}
          height={`calc(${barHeight} - 4px)`}
          maxWidth={displayType === EDisplayType.Web ? "infinity" : ""}
        />
        <Q3DSelect
          label={t("PROJECTION_METHOD")}
          items={projectionItems}
          value={projectionMode}
          setValue={(v) => {
            setProjectionMode(v as TProjectionMode);
            updateProjectionMode(v as TProjectionMode);
          }}
          height={`calc(${barHeight} - 4px)`}
          disabled={detailTab === DETAIL_TAB.PHOTO}
          maxWidth={displayType === EDisplayType.Web ? "infinity" : ""}
        />
        <Q3DInputNumber
          label={t("POINT_CLOUD_SIZE")}
          value={pointSize}
          onChange={(ps: number) => {
            setPointSize(ps);
            try {
              if (ps < 1) {
                ps = 1;
              }
              if (10 < ps) {
                ps = 10;
              }
              viewer.changePointSize(ps);
            } catch (err) {
              console.error("number error");
            }
          }}
          onBlur={(ps: number) => {
            setPointSize(viewer.getPointSize());
          }}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <ModeToggleButton
          value={currentViewerMode}
          onChange={(value: EViewerMode) => {
            updateCurrentViewerMode(value);
          }}
          disabled={detailTab === DETAIL_TAB.PHOTO}
        />
        <OverflowMenu />
      </Box>
    </Box>
  );
};
